import { RouteStep, RoutesGroup } from '@services/requests/orderService/types';
import { BranchItem } from '@services/requests/routeCalculator/locationsBranchSearchService/interfaces';
import { LocationTerminalOption } from '@services/requests/routeCalculator/locationsBranchSearchService/types';
import { ContractorData } from '@services/requests/searchLoaders/contractorLoader/queries/ContractorLoaderQuery';

import { RouteWidgetBaseParameters } from '../../../types';
import { generateLocalizedName } from '../helpers';
import { RouteShoulderProps } from './RouteShoulder';

/**
 * Сбор всех справок о маршрутах подрядчиков в 
 * локализованной или дефолтной форме
 * и фильтрация пустых значений, которые содержат
 * только теги и пробелы. Возвращает массив строк.
 * @param contractors
 * @param route
 * @param langId
 */
export const getRouteInformationList = (
  contractors: ContractorData[],
  route: RoutesGroup,
  langId: string
) => {
  // Поиск всех сущностей подрядчиков и устранение дубликатов
  const routeContractors = Array.from(
    new Set(
      route.routes.map((route) => route.steps.map((step) => step.shoulder.contractor_id)).flat()
    )
  )
    .map((contractorId) => contractors.find((contractor) => contractor.id === contractorId))
    .filter(Boolean);

  // Проверка и фильтрация всех пустых справок о маршрутах, а также поиск локализации
  // для справок или использование дефолтной
  const routeInformations = routeContractors
    .map((contractor) => {
      const { route_information, localized_route_information_data } = contractor;
      const foundLocalization = localized_route_information_data.find((l) => l.lang_id === langId);
      const localizedRouteInformation = foundLocalization?.message ?? '';

      return localizedRouteInformation.replace(/<[^>]*>|\s+/g, '').length > 0
        ? localizedRouteInformation
        : route_information;
    })
    .filter(Boolean);

  return routeInformations;
};

export const getContainerRentDescription = (route: RoutesGroup, rentName: string) => {
  const isCOC = !!route.routes.find(
    (route) => !!route.steps.find((step) => step.shoulderOffer.container_affiliation_id === 1)
  );

  const isContainerRent = !!route.routes.find((r) => !!r.containerRent);

  return `${isCOC ? 'COC' : 'SOC'}${isContainerRent ? ' / ' + rentName : ''}`;
};

/**
 * Генерация данных по плечам маршрутов
 * @param steps
 * @param startLocation
 * @param endLocation
 * @param terminals
 * @param baseData
 * @param primaryLanguageId
 */
export const generateStepsData = (
  steps: RouteStep[],
  startLocation: BranchItem,
  endLocation: BranchItem,
  terminals: BranchItem[],
  baseData: RouteWidgetBaseParameters,
  primaryLanguageId = ''
): RouteShoulderProps[] =>
  steps
    .map((step) => {
      const isStartFromTerminal = !!step.startTerminal;

      let startPoint = { ...startLocation };
      if (isStartFromTerminal) {
        const terminal = terminals.find((t) => t.id === step.startTerminal.id);
        if (terminal) {
          startPoint = { ...terminal };
        }
      }

      const isEndFromTerminal = !!step.endTerminal;

      let endPoint = { ...endLocation };
      if (isEndFromTerminal) {
        const terminal = terminals.find((t) => t.id === step.endTerminal.id);
        if (terminal) {
          endPoint = { ...terminal };
        }
      }

      const stepShoulderType = baseData.shoulderTypes.find(
        (s) => s.id === step.shoulder.shoulder_type
      );

      const contractor = baseData.contractors.find((c) => c.id === step.shoulder.contractor_id);

      const assignments = step.shoulderOfferAssignments ?? [];
      const firstAssignment = assignments[0];

      let stepCargoType = '';

      if (firstAssignment) {
        switch (firstAssignment.transportKind) {
          case 'container':
            stepCargoType = 'FCL';
            break;
          case 'truck':
            stepCargoType = 'FTL';
            break;
        }
      }

      const contractorName =
        (contractor &&
          generateLocalizedName(
            contractor.default_name,
            contractor.localized_names_data,
            primaryLanguageId
          )) ||
        '';

      const baseStepData = {
        deliveryDays: step.shoulderOffer.delivery_time,
        endContractorName: contractorName,
        endLocation: endPoint,
        routeStepCargoTypes: [stepCargoType],
        routeStepTypes: [`${stepShoulderType?.transport_type}`],
        startContractorName: contractorName,
        startLocation: startPoint,
      };

      if (stepShoulderType?.transport_type) {
        return [baseStepData];
      }

      return step.shoulder.shoulderSteps.map((shoulderStep, idx) => {
        switch (idx) {
          case 0:
            return {
              ...baseStepData,
              routeStepTypes: [shoulderStep.transportType.id],
              endLocation:
                terminals.find((t) => t.id === shoulderStep.endTerminal?.id) ??
                new LocationTerminalOption(),
            };
          case step.shoulder.shoulderSteps.length - 1:
            return {
              ...baseStepData,
              routeStepTypes: [shoulderStep.transportType.id],
              deliveryDays: 0,
              startLocation:
                terminals.find((t) => t.id === shoulderStep.startTerminal?.id) ??
                new LocationTerminalOption(),
            };
        }

        return {
          ...baseStepData,
          routeStepTypes: [shoulderStep.transportType.id],
          deliveryDays: 0,
          startLocation:
            terminals.find((t) => t.id === shoulderStep.startTerminal?.id) ??
            new LocationTerminalOption(),
          endLocation:
            terminals.find((t) => t.id === shoulderStep.endTerminal?.id) ??
            new LocationTerminalOption(),
        };
      });
    })
    .flat();
